import { useState, useCallback } from 'react';
import { ChevronDown } from 'lucide-react';
import { servicesData } from '../data/servicesData';
import type { LucideIcon } from 'lucide-react';
import Modal from './Modal';

interface Service {
  title: string;
  description: string[];
  icon: LucideIcon;
}

interface ServicesModalProps {
  isOpen: boolean;
  onClose: () => void;
}

function AccordionItem({
  service,
  index,
  isOpen,
  onToggle
}: {
  service: Service;
  index: number;
  isOpen: boolean;
  onToggle: (index: number) => void;
}) {
  return (
    <div className="border-2 border-aurore-light dark:border-warm-gray-700 rounded-xl overflow-hidden hover:border-aurore-dark transition-colors">
      <button
        onClick={() => onToggle(index)}
        className="w-full flex items-center justify-between p-4 bg-white dark:bg-warm-gray-900 hover:bg-aurore-light/30 dark:hover:bg-warm-gray-800 transition-colors text-left"
        aria-expanded={isOpen}
        aria-controls={`accordion-content-${index}`}
      >
        <div className="flex items-center space-x-3 pr-4">
          <service.icon
            className="w-5 h-5 text-aurore-dark dark:text-aurore-light flex-shrink-0"
            aria-hidden="true"
          />
          <h3 className="text-lg font-semibold text-gray-900 dark:text-warm-gray-100">
            {service.title}
          </h3>
        </div>
        <ChevronDown
          className={`w-5 h-5 text-aurore-dark dark:text-aurore-light flex-shrink-0 transition-transform duration-300 ${
            isOpen ? 'rotate-180' : ''
          }`}
          aria-hidden="true"
        />
      </button>

      <div
        id={`accordion-content-${index}`}
        className={`grid transition-all duration-500 ease-in-out ${
          isOpen ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
        }`}
        role="region"
      >
        <div className="overflow-hidden">
          <div className="p-4 bg-aurore-light/20 dark:bg-warm-gray-800/50 space-y-2">
            {service.description.map((item, idx) => (
              <div key={idx} className="flex items-start space-x-3">
                <span
                  className="text-aurore-dark font-bold text-lg leading-none mt-1"
                  aria-hidden="true"
                >
                  ✓
                </span>
                <p className="text-gray-700 dark:text-warm-gray-300 leading-relaxed">{item}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function ServicesModal({ isOpen, onClose }: ServicesModalProps) {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const toggleAccordion = useCallback((index: number) => {
    setOpenIndex(prev => prev === index ? null : index);
  }, []);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Services pour Particuliers"
    >
      <div className="p-6 space-y-4">
        {servicesData.particuliers.map((service, index) => (
          <AccordionItem
            key={index}
            service={service}
            index={index}
            isOpen={openIndex === index}
            onToggle={toggleAccordion}
          />
        ))}
      </div>
    </Modal>
  );
}
